import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Landmark, Mail, Plus, Receipt, ShieldAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { IncidentStatusBadge, InvoiceStatusBadge, ReportIncidentModal } from '../../components/device-history';
import { Badge, Button, Card, EmptyState, ErrorState, Input, PageHeader, PageLoader, Pagination, Select, Table, Tabs, Td, Th } from '../../components/ui';
import { api } from '../../lib/api';
import { formatDate, formatMoney, formatUtcDate, personName } from '../../lib/format';
import { useDebouncedValue, useUrlState } from '../../lib/hooks';
import {
  INCIDENT_STATUS_LABEL,
  INCIDENT_TYPE_LABEL,
  INVOICE_STATUS_LABEL,
  type DamageInvoiceStatus,
  type DamageInvoiceSummary,
  type IncidentStatus,
  type IncidentSummary,
  type IncidentType,
} from '../../lib/incidents';
import type { Page } from '../../lib/types';

const PAGE_SIZE = 25;

type ListState = {
  tab: string;
  status: string;
  type: string;
  invoiceStatus: string;
  search: string;
  page: string;
};
type SetListState = (patch: Partial<ListState>, options?: { resetPage?: boolean }) => void;

function SearchBox({ value, onChange, placeholder }: { value: string; onChange(value: string): void; placeholder: string }) {
  const [text, setText] = useState(value);
  const debounced = useDebouncedValue(text.trim(), 300);
  useEffect(() => {
    if (debounced !== value) onChange(debounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  return <Input value={text} onChange={(event) => setText(event.target.value)} placeholder={placeholder} className="sm:w-72" />;
}

/** Incidents on school devices, and the damage invoices sent for them. */
export function IncidentsPage() {
  const [state, setState] = useUrlState<ListState>({ tab: 'incidents', status: 'ACTIVE', type: '', invoiceStatus: '', search: '', page: '1' });
  const [reporting, setReporting] = useState(false);
  const tab = state.tab === 'invoices' ? 'invoices' : 'incidents';

  return (
    <>
      <PageHeader
        title="Incidents & billing"
        description="Damage, loss, and theft on school devices, and what families are billed for it."
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => setReporting(true)}>
            Report incident
          </Button>
        }
      />
      <Tabs
        className="mb-6"
        value={tab}
        onChange={(value) => setState({ tab: value, search: '' })}
        tabs={[
          { value: 'incidents', label: 'Incidents' },
          { value: 'invoices', label: 'Invoices' },
        ]}
      />
      {tab === 'incidents' ? <IncidentList key="incidents" state={state} setState={setState} /> : <InvoiceList key="invoices" state={state} setState={setState} />}
      <ReportIncidentModal open={reporting} onClose={() => setReporting(false)} />
    </>
  );
}

function IncidentList({ state, setState }: { state: ListState; setState: SetListState }) {
  const navigate = useNavigate();
  const page = Number(state.page) || 1;
  const query = useQuery({
    queryKey: ['incidents', state.status, state.type, state.search, page],
    queryFn: () =>
      api.get<Page<IncidentSummary>>('/incidents', {
        status: state.status || undefined,
        type: state.type || undefined,
        search: state.search || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-4">
        <SearchBox value={state.search} onChange={(search) => setState({ search })} placeholder="Search by number, device, or person" />
        <Select className="sm:w-44" value={state.status} onChange={(event) => setState({ status: event.target.value })} aria-label="Status">
          <option value="ACTIVE">Not closed</option>
          <option value="">Every status</option>
          {(Object.keys(INCIDENT_STATUS_LABEL) as IncidentStatus[]).map((status) => (
            <option key={status} value={status}>
              {INCIDENT_STATUS_LABEL[status]}
            </option>
          ))}
        </Select>
        <Select className="sm:w-48" value={state.type} onChange={(event) => setState({ type: event.target.value })} aria-label="Type">
          <option value="">Every type</option>
          {(Object.keys(INCIDENT_TYPE_LABEL) as IncidentType[]).map((type) => (
            <option key={type} value={type}>
              {INCIDENT_TYPE_LABEL[type]}
            </option>
          ))}
        </Select>
      </div>

      {query.isLoading ? (
        <PageLoader />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.data!.items.length === 0 ? (
        <EmptyState icon={<ShieldAlert />} title="No incidents" description="Report damage, loss, or theft here, on a device, or on a work order." />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Incident</Th>
                <Th>Device</Th>
                <Th>Status</Th>
                <Th>Who had it</Th>
                <Th>When</Th>
                <Th>Invoices</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {query.data!.items.map((incident) => (
                <tr key={incident.id} className="cursor-pointer hover:bg-slate-50/80" onClick={() => navigate(`/incidents/${incident.id}`)}>
                  <Td className="whitespace-nowrap">
                    <Link to={`/incidents/${incident.id}`} onClick={(event) => event.stopPropagation()} className="font-medium text-slate-900 hover:text-brand-700">
                      #{incident.number}
                    </Link>
                    <p className="text-xs text-slate-500">
                      {INCIDENT_TYPE_LABEL[incident.type]}
                      {incident.photoCount > 0 && ` · ${incident.photoCount} ${incident.photoCount === 1 ? 'photo' : 'photos'}`}
                    </p>
                  </Td>
                  <Td>
                    <span className="font-mono text-xs font-semibold text-slate-900">{incident.asset.assetTag}</span>
                    <p className="max-w-[14rem] truncate text-xs text-slate-500">{incident.asset.name}</p>
                  </Td>
                  <Td>
                    <IncidentStatusBadge status={incident.status} />
                  </Td>
                  <Td className="whitespace-nowrap">{incident.responsiblePerson ? personName(incident.responsiblePerson) : '—'}</Td>
                  <Td className="whitespace-nowrap">{formatDate(incident.occurredAt)}</Td>
                  <Td>
                    {incident.invoices.length === 0 ? (
                      '—'
                    ) : (
                      <div className="flex flex-col gap-1">
                        {incident.invoices.map((invoice) => (
                          <span key={invoice.id} className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap">
                            #{invoice.number} · {formatMoney(invoice.total)} <InvoiceStatusBadge status={invoice.status} />
                          </span>
                        ))}
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination page={page} pageSize={PAGE_SIZE} total={query.data!.total} onPage={(next) => setState({ page: String(next) }, { resetPage: false })} />
        </>
      )}
    </Card>
  );
}

function InvoiceList({ state, setState }: { state: ListState; setState: SetListState }) {
  const navigate = useNavigate();
  const page = Number(state.page) || 1;
  const query = useQuery({
    queryKey: ['invoices', state.invoiceStatus, state.search, page],
    queryFn: () =>
      api.get<Page<DamageInvoiceSummary>>('/invoices', {
        status: state.invoiceStatus || undefined,
        search: state.search || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-4">
        <SearchBox value={state.search} onChange={(search) => setState({ search })} placeholder="Search by number, student, or device" />
        <Select className="sm:w-44" value={state.invoiceStatus} onChange={(event) => setState({ invoiceStatus: event.target.value })} aria-label="Status">
          <option value="">Every status</option>
          <option value="OUTSTANDING">Unpaid</option>
          {(Object.keys(INVOICE_STATUS_LABEL) as DamageInvoiceStatus[]).map((status) => (
            <option key={status} value={status}>
              {INVOICE_STATUS_LABEL[status]}
            </option>
          ))}
        </Select>
      </div>

      {query.isLoading ? (
        <PageLoader />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.data!.items.length === 0 ? (
        <EmptyState icon={<Receipt />} title="No invoices" description="Create an invoice from an incident to bill a family for damage." />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Invoice</Th>
                <Th>Student</Th>
                <Th>Device</Th>
                <Th className="text-right">Total</Th>
                <Th>Status</Th>
                <Th>Due</Th>
                <Th>Sent</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {query.data!.items.map((invoice) => (
                <tr key={invoice.id} className="cursor-pointer hover:bg-slate-50/80" onClick={() => navigate(`/invoices/${invoice.id}`)}>
                  <Td className="whitespace-nowrap">
                    <Link to={`/invoices/${invoice.id}`} onClick={(event) => event.stopPropagation()} className="font-medium text-slate-900 hover:text-brand-700">
                      #{invoice.number}
                    </Link>
                    <p className="text-xs text-slate-500">Incident #{invoice.incident.number}</p>
                  </Td>
                  <Td className="whitespace-nowrap">
                    {invoice.studentName}
                    {invoice.billToName && <p className="text-xs text-slate-500">{invoice.billToName}</p>}
                  </Td>
                  <Td>
                    <span className="font-mono text-xs font-semibold text-slate-900">{invoice.incident.asset.assetTag}</span>
                    <p className="max-w-[12rem] truncate text-xs text-slate-500">{invoice.incident.asset.name}</p>
                  </Td>
                  <Td className="text-right font-medium whitespace-nowrap text-slate-900">{formatMoney(invoice.total)}</Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      <InvoiceStatusBadge status={invoice.status} />
                      {invoice.overdue && <Badge tone="red">Overdue</Badge>}
                    </div>
                  </Td>
                  <Td className="whitespace-nowrap">{formatUtcDate(invoice.dueOn)}</Td>
                  <Td>
                    <div className="flex items-center gap-2 text-slate-400">
                      {invoice.emailedAt && (
                        <span title={`Emailed ${formatDate(invoice.emailedAt)}`}>
                          <Mail className="size-4" />
                        </span>
                      )}
                      {invoice.blackbaudPostedAt && (
                        <span title={`Posted to Blackbaud ${formatDate(invoice.blackbaudPostedAt)}`}>
                          <Landmark className="size-4" />
                        </span>
                      )}
                      {!invoice.emailedAt && !invoice.blackbaudPostedAt && '—'}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination page={page} pageSize={PAGE_SIZE} total={query.data!.total} onPage={(next) => setState({ page: String(next) }, { resetPage: false })} />
        </>
      )}
    </Card>
  );
}
